/**
 * FeatureCard component for marketing pages
 * Reusable feature card with icon, title, and description
 */

import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FEATURE_COLORS, type FeatureColor } from '@/lib/design-tokens';
import { IconWrapper } from './icon-wrapper';

export interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  color?: FeatureColor;
  className?: string;
  id?: string;
}

export function FeatureCard({
  icon,
  title,
  description,
  color = 'blue',
  className = '',
  id,
}: FeatureCardProps) {
  const colorConfig = FEATURE_COLORS[color];
  const cardId = id || `feature-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <article
      id={cardId}
      className={cn(
        'surface-card surface-card-hover rounded-2xl p-8',
        `hover:shadow-2xl ${colorConfig.hoverShadow} ${colorConfig.hoverBorder}`,
        className
      )}
      aria-labelledby={`${cardId}-title`}
    >
      <IconWrapper icon={icon} color={color} size="md" />
      <h3 id={`${cardId}-title`} className="font-display text-xl font-semibold tracking-tight mb-2">
        {title}
      </h3>
      <p className="text-muted-foreground leading-relaxed">{description}</p>
    </article>
  );
}
